<script setup lang="ts">
const props = defineProps<{
  name: string
}>()

const { moves, game } = useBoard(props.name)

interface TournamentItemProps {
  renjuNetId: string,
  name: string,
  start: string
}

const items = ref<TournamentItemProps[]>([])

const selectedTournament = ref<TournamentItemProps | null>(null)

const computedLastMoves = computed(() => {
  const newLastMoves = moves.value
    .filter(move => move.number && move.number > 0)
    .map((move) => {
      return {
        value: `${move.color}-${move.number}`,
        label: move.color === 'black' ? `黒${move.number}` : `白${move.number}`
      }
    })

  const splitEndNumber = game.value.endNumber.split('-')
  if (
    game.value.endNumber !== '-' &&
    splitEndNumber.length === 2 &&
    !newLastMoves.some(lastMove => lastMove.value === game.value.endNumber)
  ) {
    newLastMoves.push({
      value: game.value.endNumber,
      label: splitEndNumber[0] === 'black' ? `黒${splitEndNumber[1]}` : `白${splitEndNumber[1]}`
    })
  }

  return newLastMoves.concat([{ value: '-', label: '-' }]).reverse()
})

const handleSearch = (text: string) => {
  if (text.length >= 3) {
    (async () => {
      const response = await $fetch<{ tournaments: TournamentItemProps[] }>('/api/renjunet/tournament', {
        query: {
          query: text
        }
      })
      items.value = response.tournaments.sort((a, b) => {
        const dateA = new Date(a.start)
        const dateB = new Date(b.start)
        return dateA < dateB ? 1 : -1
      })
    })()
  } else {
    items.value = []
  }
}

function handleUpdateTournament (newValue: TournamentItemProps) {
  if (!newValue) {
    game.value.tournament.renjuNetId = ''
    game.value.title = ''
  } else {
    game.value.tournament.renjuNetId = newValue.renjuNetId
    game.value.title = newValue.name
  }
}

watch(game, async (newValue) => {
  if (newValue.tournament.renjuNetId) {
    const response = await $fetch<{ tournaments: TournamentItemProps[] }>('/api/renjunet/tournament', {
      query: {
        renjuNetId: newValue.tournament.renjuNetId
      }
    })
    items.value = items.value.concat(response.tournaments)
    if (!selectedTournament.value?.renjuNetId) {
      selectedTournament.value = response.tournaments[0]
    }
  }
}, { immediate: true, deep: true })
</script>
<template>
  <v-window-item value="information">
    <v-card flat>
      <v-card-text>
        <v-autocomplete
          v-model="selectedTournament"
          label="棋戦 (RenjuNet)"
          :items="items"
          item-title="name"
          item-value="name"
          return-object
          no-data-text="検索結果はありません。"
          clearable
          variant="outlined"
          dirty
          @update:search="handleSearch"
          @update:model-value="handleUpdateTournament"
        >
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :title="item?.raw?.name"
              :subtitle="`${item.raw.start}`"
            />
          </template>
        </v-autocomplete>
        <v-text-field
          v-model="game.title"
          label="タイトル"
          variant="outlined"
          class="w-100"
          :disabled="Boolean(selectedTournament)"
          dirty
        />
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="game.round"
              label="ラウンド"
              variant="outlined"
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="game.date"
              label="対局日"
              type="date"
              variant="outlined"
              hide-details
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-select
              v-model="game.endNumber"
              label="最終手"
              :items="computedLastMoves"
              item-title="label"
              item-value="value"
              variant="outlined"
              hide-details
              dirty
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="game.result"
              label="結果"
              :items="gameResultList"
              item-title="label"
              item-value="value"
              variant="outlined"
              hide-details
              dirty
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-window-item>
</template>
